import { startTransition } from 'react'
import { getFirAuth } from '../../Api/firebase/firebaseConnect'
import { formatUpdatedUserData } from '../../Api/firebase/AuthData'
import { createUser, getUser, mapFavorites } from '../../Api/Users'
import { initBrazeCore } from '../../Config/Braze/braze.config'
import { validateAuthState, getUserId, USER_AUTH_ISSUES } from './authHelpers'
import { ANALYTICS_TAGS } from '../../Constant'
import { BRAZE_EVENTS, type MasterLinkParams } from '../../Constant'
import type { User } from '../../Api/Users/types'

import {
  logEvent,
  logBrazeEvent,
  normalizeError,
  executeWithRetry,
  withTimeout,
  determineMasterParamsToAdd,
  NETWORK_CHECK_DELAY,
  DB_OPERATION_TIMEOUT,
} from '../index'
import { isNavigatorOnline } from '../networking/networkHelpers'

/**
 * Check if user is first time user based on Firebase metadata
 */
export const checkFirstTimeUser = async (): Promise<boolean> => {
  try {
    const auth = await getFirAuth()
    const user = auth?.currentUser
    if (!user?.metadata?.creationTime || !user?.metadata?.lastSignInTime) return false

    const lastSignInTime = new Date(user.metadata.lastSignInTime).getTime()
    const creationTime = new Date(user.metadata.creationTime).getTime()
    const secondsSinceCreation = (lastSignInTime - creationTime) / 1000

    return secondsSinceCreation < 5
  } catch (error) {
    console.error('Error checking first time user:', error)
    logEvent(ANALYTICS_TAGS.first_time_user_check_error, {
      error: normalizeError(error),
      product: 'web',
    })
    return false
  }
}

/**
 * Fetch user from PLL database with validation and error handling
 */
export const fetchUserFromDB = async (
  firUsr: User,
  getMasterLinkParamsFromLocal: () => MasterLinkParams | null,
  toggleShowSnackbar: (options: {
    text: string
    color: 'red' | 'blue' | 'green'
  }) => void
): Promise<User | null> => {
  try {
    if (!isNavigatorOnline()) {
      console.warn('Network offline, cannot fetch user from DB')
      logEvent(ANALYTICS_TAGS.user_db_fetch_error, {
        error: 'Network offline',
        userId: getUserId(firUsr),
        product: 'web',
      })

      await new Promise((resolve) => setTimeout(resolve, NETWORK_CHECK_DELAY))
      if (!isNavigatorOnline()) {
        toggleShowSnackbar({
          text: 'Network connection required. Please check your connection.',
          color: 'red',
        })
        return null
      }
    }

    const dbUser = await executeWithRetry(
      () => fetchUserFromPLLDB(firUsr, getMasterLinkParamsFromLocal),
      `fetchUserFromDB_${getUserId(firUsr)}`
    )

    if (dbUser) {
      const { isValid, issues } = validateAuthState(dbUser)
      if (!isValid) {
        const hasSyncIssue = issues.includes(
          USER_AUTH_ISSUES.USER_MARKED_AS_LOGGED_IN_BUT_NO_ID
        )

        if (hasSyncIssue && issues.length === 1) {
          // This is just a Firebase/DB sync issue - fix it
          dbUser.isLoggedIn = true
        } else {
          console.error('Invalid user data from DB:', issues)
          logEvent(ANALYTICS_TAGS.user_db_fetch_error, {
            error: 'Invalid user data from DB',
            issues: issues,
            userId: getUserId(firUsr),
            product: 'web',
          })
          return null
        }
      }
    }

    return dbUser
  } catch (error) {
    console.error('Error fetching user from database:', error)
    logEvent(ANALYTICS_TAGS.user_db_fetch_error, {
      error: normalizeError(error),
      userId: getUserId(firUsr),
      product: 'web',
    })
    return null
  }
}

/**
 * Fetch user from PLL database with retry logic
 */
export const fetchUserFromPLLDB = async (
  firUsr: User,
  getMasterLinkParamsFromLocal: () => MasterLinkParams | null
): Promise<User | null> => {
  let linkParams: MasterLinkParams | null = null
  const isNewUser = await checkFirstTimeUser()
  const mParams = getMasterLinkParamsFromLocal()

  if (mParams && Object.keys(mParams).length > 0) {
    linkParams = determineMasterParamsToAdd(mParams, isNewUser)
  }

  if (isNewUser) {
    return await withTimeout(
      createUser(firUsr, linkParams),
      DB_OPERATION_TIMEOUT,
      'createUser'
    )
  } else {
    const user = await withTimeout(getUser(linkParams), DB_OPERATION_TIMEOUT, 'getUser')

    if (!user) {
      logEvent(ANALYTICS_TAGS.firebase_user_retry_createUser, {
        product: 'web',
        user: getUserId(firUsr),
      })
      linkParams = determineMasterParamsToAdd(mParams, true)
      const retryUser = await withTimeout(
        createUser(firUsr, linkParams),
        DB_OPERATION_TIMEOUT,
        'createUser_retry'
      )
      if (!retryUser) {
        logEvent(ANALYTICS_TAGS.firebase_user_retry_createUser_failed, {
          product: 'web',
          user: getUserId(firUsr),
        })
      }
      return retryUser
    }
    return user
  }
}

/**
 * Set user data in state with proper error handling
 */
export const setUserDataInState = async (
  userRes: User,
  setUser: (user: User) => void,
  setUserFavorites: (favorites: any) => void,
  setAuthLoading: (loading: boolean) => void
): Promise<void> => {
  try {
    const userAuth = await formatUpdatedUserData(userRes)
    const userFavs = mapFavorites(userRes)
    startTransition(() => {
      setUser(userAuth)
      setUserFavorites(userFavs)
      setAuthLoading(false)
    })

    if (userAuth?.uid) {
      try {
        const success = await initBrazeCore(userAuth.uid)
        if (success) {
          logBrazeEvent(ANALYTICS_TAGS.log_in)
          logEvent(ANALYTICS_TAGS.log_in)
        }
      } catch (brazeError) {
        console.error('Error initializing Braze:', brazeError)
        logEvent(ANALYTICS_TAGS.braze_init_error, {
          error: normalizeError(brazeError),
          userId: userAuth.uid,
          product: 'web',
        })
      }
    }
  } catch (error) {
    console.error('Error setting user data in state:', error)
    logEvent(ANALYTICS_TAGS.user_state_set_error, {
      error: normalizeError(error),
      product: 'web',
    })
    startTransition(() => {
      setAuthLoading(false)
    })
  }
}

/**
 * Handle anonymous sign in with proper error handling
 */
export const handleAnonymousSignIn = async (): Promise<void> => {
  try {
    const success = await initBrazeCore()
    if (success) {
      logBrazeEvent(BRAZE_EVENTS.anonymous_session_start)
      logEvent(BRAZE_EVENTS.anonymous_session_start)
    }
  } catch (error) {
    console.error('Error in anonymous sign in:', error)
    logEvent(ANALYTICS_TAGS.braze_init_error, {
      error: normalizeError(error),
      context: 'handleAnonymousSignIn',
      product: 'web',
    })
  }
}
